import { useMemo, useRef } from 'react'
import { useStore, childrenIndex } from '../core/store'
import { computeVisible, focusSet } from '../core/visibility'
import { readPalette, type Palette } from '../core/palette'
import { CUE_EMPHASIS } from '../core/cues'
import { weightedNodes } from '../core/weight'
// The two helpers every coach surface reads, from the leaf module that holds
// them rather than from `ui/Coach.tsx`. This is a one-way edge — `ui/coachView`
// imports nothing from `canvas/` — and it exists because the alternative is two
// places deciding which cue is current, and a highlight lighting up a subtree
// the card is not talking about.
import { coachGateOpen, liveCoach, visibleCues } from '../ui/coachView'

/**
 * Everything the canvas and the label layer both need, derived once.
 *
 * The palette is read from CSS rather than duplicated in JS, so a token change
 * lands on the canvas and the chrome together. It is re-read only on theme
 * change — `getComputedStyle` is far too slow to touch per frame.
 */
export function useGraphFrame() {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const filters = useStore((s) => s.filters)
  const playhead = useStore((s) => s.playhead)
  const focusId = useStore((s) => s.focusId)
  // The footer's "Dim unrelated" (D369): with it on, the claim held dims the
  // graph the way Focus does — everything outside its lineage and children —
  // without being a focus. Subscribed only while the switch is on, so a
  // selection with it off re-renders nothing here, as before.
  const dimSelected = useStore((s) => (s.dimUnrelated ? s.selectedId : null))
  // Through `liveCoach`, like the card, the bar, the pins and the director: the
  // clamp below has to be the SAME clamp they use, and it would stop being that
  // the moment a coach belonging to another session answered here and nowhere
  // else. `cueList` is already empty for a foreign coach, so nothing lights up
  // today either way — this keeps one reading of "which coach" rather than two
  // that happen to agree.
  const coach = useStore(liveCoach)
  const coachGate = useStore(coachGateOpen)
  // Subscribed, not derived here: `visibleCues` is memoized on its inputs, so
  // this is one identity comparison per store write and it re-renders exactly
  // when the answer changes — including when a price change reorders the
  // CHEAPEST lens, which no `session`/`rev` dependency would have caught.
  const cueList = useStore(visibleCues)
  const theme = useStore((s) => s.theme)
  const weighting = useStore((s) => s.weighting)

  // Keyed on the two fields each of these reads, and NOT on `rev`.
  //
  // `setViewInset` bumps `rev` for a pane toggle or a window resize, swapping no
  // node and no claim — so every memo below rebuilt an O(n) map each time
  // somebody dragged a divider, `weightedNodes` cloning the whole node map with
  // it. `session` identity is no better: it is replaced for spend rows, runs and
  // research records that none of these read.
  //
  // Safe to narrow because the store says so at `forkAtStep`: "every writer in
  // this file replaces them rather than mutating in place". Nodes and order are
  // therefore new objects whenever they change, which is exactly the question
  // these four ask. AGENTS.md states the rule for the five derived maps and it
  // is the same one — key on the identity of every field a write would swap, and
  // on nothing else.
  const childrenOf = useMemo(() => childrenIndex(session), [session.nodes, session.order])
  // What the RENDERER draws. Under `model` the session itself, by identity;
  // under `evidence` the same session with every node's two size channels
  // replaced by what the records about it say — `core/weight.ts`. Only the
  // drawing reads it: the visibility filters, the focus set and the guide walk
  // below stay on the real session, so a switch on the key bar changes how a
  // claim is drawn and never which claims are on screen.
  //
  // In TWO memos, because the expensive half and the cheap half have different
  // inputs: `weightedNodes` reads the node map and the lens, while composing the
  // result back onto the session reads the session. Folded together, the clone
  // was rebuilt whenever anything on the session moved.
  const weighted = useMemo(
    () => (weighting === 'model' ? null : weightedNodes(session.nodes, 'evidence')),
    [session.nodes, weighting],
  )
  const drawn = useMemo(
    () => (weighted ? { ...session, nodes: weighted } : session),
    [session, weighted],
  )
  // One memo, so `visible` and `folded` can never disagree about the filters.
  const vis = useMemo(
    () => computeVisible(session, filters, childrenOf, playhead),
    [session.nodes, session.order, filters, childrenOf, playhead],
  )
  // A focus the reader set outranks the switch: it is the one they chose, and
  // it survives the selection moving.
  const focusFrom = focusId ?? dimSelected
  const focus = useMemo(
    () => (focusFrom ? focusSet(session, childrenOf, focusFrom) : null),
    [session.nodes, session.order, childrenOf, focusFrom],
  )
  // The coach is the ONLY author of the highlight now, and the cue the card is
  // showing is the one it names.
  //
  // One reading of "which cue", shared with the card and the director — the same
  // clamp, because the visible list is derived and its length is not in state.
  const coachCue =
    coachGate && coach?.open && cueList.length > 0
      ? cueList[Math.min(Math.max(0, coach.index), cueList.length - 1)]
      : undefined

  // No fallback arm behind it, deliberately. THE DIM IS A SURFACE, not a
  // separate thing: it says "look here" and the card says why, so a dim with no
  // card behind it darkens the whole graph around a node nobody can read,
  // advance or put away. They go dark together.
  //
  // Memoized so the walk below keeps its old cadence — one recompute per change,
  // not one per render — and so target and emphasis stay one pair: the cone is
  // the reason the highlight is legible, and mixing an anchor from one reading
  // with an emphasis from another would light the wrong half of the graph.
  const guide = useMemo(
    (): { targetId: string; emphasis: 'lineage' | 'dependencies' } | undefined =>
      coachCue ? { targetId: coachCue.anchorId, emphasis: CUE_EMPHASIS[coachCue.kind] } : undefined,
    [coachCue],
  )
  const guideTargetId = guide?.targetId ?? null
  const guideKeep = useMemo(() => {
    if (!guide || !session.nodes[guide.targetId]) return null
    const out = new Set<string>()

    // Hold every premise that reaches the target. A synthesis can have a second
    // local parent, so this is a guarded stack rather than one parent pointer.
    const up = [guide.targetId]
    while (up.length) {
      const id = up.pop()!
      if (out.has(id)) continue
      const n = session.nodes[id]
      if (!n) continue
      out.add(id)
      if (n.parentId) up.push(n.parentId)
      for (const parent of n.bridgeFrom ?? []) up.push(parent)
    }

    // A pressure point earns attention because of what rests on it. Keep that
    // dependency cone visible as well as the route that led to it.
    if (guide.emphasis === 'dependencies') {
      const bridgeChildren = new Map<string, string[]>()
      for (const id of session.order) {
        const node = session.nodes[id]
        if (!node) continue
        for (const parent of node.bridgeFrom ?? []) {
          const list = bridgeChildren.get(parent)
          if (list) list.push(id)
          else bridgeChildren.set(parent, [id])
        }
      }
      const down = [
        ...(childrenOf.get(guide.targetId) ?? []),
        ...(bridgeChildren.get(guide.targetId) ?? []),
      ]
      while (down.length) {
        const id = down.pop()!
        if (out.has(id)) continue
        if (!session.nodes[id]) continue
        out.add(id)
        for (const child of childrenOf.get(id) ?? []) down.push(child)
        for (const child of bridgeChildren.get(id) ?? []) down.push(child)
      }
    }
    return out
    // The guide walk reads the same two fields as the four above.
  }, [guide, session.nodes, session.order, childrenOf])

  const paletteRef = useRef<{ theme: string; value: Palette } | null>(null)
  const palette = useMemo(() => {
    if (paletteRef.current?.theme !== theme) {
      paletteRef.current = { theme, value: readPalette(theme) }
    }
    return paletteRef.current.value
  }, [theme])

  return {
    session: drawn,
    rev,
    childrenOf,
    visible: vis.visible,
    folded: vis.folded,
    focus,
    guideKeep,
    guideTargetId,
    palette,
    filters,
  }
}
